<!--
  Who's signed in, with Member view (treasurer only), Switch account and Sign out.
  "side" sits at the bottom of the sidebar; "avatar" is a round button for the phone's top bar.
-->
<script lang="ts">
  import Drawer from "./Drawer.svelte";
  import { app } from "../lib/store.svelte.ts";
  import { member } from "../lib/member.svelte.ts";
  import { router } from "../lib/router.svelte.ts";
  import { initials } from "../lib/format.ts";
  import { privacy } from "../lib/privacy.svelte.ts";
  import { passkeyError, signInWithPasskey } from "../lib/auth.ts";

  let { variant = "side" }: { variant?: "side" | "avatar" } = $props();
  let open = $state(false);
  let busy = $state(false);

  const isOwner = $derived(app.role === "owner");
  const roleLine = $derived(
    !isOwner
      ? "Member: you see your own items in each group you've joined."
      : app.viewAsMember
        ? "Treasurer, in Member view: you're seeing only your own items."
        : "Treasurer: you see and manage everything.",
  );

  function toggleMemberView() {
    app.setViewAsMember(!app.viewAsMember);
    open = false;
    router.go("/");
  }

  async function switchAccount() {
    busy = true;
    await app.signOut();
    member.clear();
    try {
      await signInWithPasskey();
    } catch (e) {
      app.toast(passkeyError(e), "error");
    }
    await app.refreshAccount();
    busy = false;
    open = false;
    router.go("/");
  }

  async function signOut() {
    await app.signOut();
    member.clear();
    open = false;
    router.go("/");
  }
</script>

{#if variant === "side"}
  <button class="side-btn" onclick={() => (open = true)} aria-haspopup="dialog">
    <span class="avatar" aria-hidden="true">{initials(app.userName || "?")}</span>
    <span class="who">
      <span class="name">{app.userName}</span>
      <span class="role">{isOwner ? (app.viewAsMember ? "Member view" : "Treasurer") : "Member"}</span>
    </span>
  </button>
{:else}
  <button class="avatar-btn" onclick={() => (open = true)} aria-label="Account: {app.userName}" aria-haspopup="dialog">
    <span class="avatar" class:viewing={app.viewAsMember}>{initials(app.userName || "?")}</span>
  </button>
{/if}

{#if open}
  <Drawer title="Account" onclose={() => (open = false)}>
    <div class="menu">
      <div class="me">
        <span class="avatar big" aria-hidden="true">{initials(app.userName || "?")}</span>
        <div>
          <p class="name">{app.userName}</p>
          <p class="muted small">{roleLine}</p>
        </div>
      </div>

      {#if isOwner}
        <button class="item" role="switch" aria-checked={app.viewAsMember} onclick={toggleMemberView}>
          <span>
            <span class="label">Member view</span>
            <span class="muted small">See the app the way your friends do: only your own items, no tax details.</span>
          </span>
          <span class="switch" class:on={app.viewAsMember} aria-hidden="true"><span></span></span>
        </button>
      {/if}

      <button class="item" role="switch" aria-checked={privacy.on} onclick={() => privacy.set(!privacy.on)}>
        <span>
          <span class="label">Privacy mode</span>
          <span class="muted small">For sharing your screen: amounts and names are blurred until you hover or tap them.</span>
        </span>
        <span class="switch" class:on={privacy.on} aria-hidden="true"><span></span></span>
      </button>

      <button class="item" disabled={busy} onclick={switchAccount}>
        <span>
          <span class="label">Switch account</span>
          <span class="muted small">Sign in with a different passkey on this device.</span>
        </span>
      </button>
      <button class="item" onclick={() => { open = false; router.go("/you"); }}>
        <span class="label">Your settings</span>
      </button>
      <button class="item danger" onclick={signOut}><span class="label">Sign out</span></button>
    </div>
  </Drawer>
{/if}

<style>
  .avatar {
    display: inline-grid;
    place-items: center;
    flex: none;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--ink);
    color: var(--paper);
    font-size: 0.75rem;
    font-weight: 700;
  }

  .avatar.viewing {
    background: #8b5cd6;
  }

  .avatar.big {
    width: 44px;
    height: 44px;
    font-size: 1rem;
  }

  .side-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px;
    border: 0;
    border-radius: var(--r-md);
    background: none;
    color: var(--ink);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .side-btn:hover {
    background: var(--wash);
  }

  .who {
    display: grid;
    min-width: 0;
  }

  .name {
    margin: 0;
    font-weight: 650;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .role {
    font-size: var(--t-xs);
    color: var(--muted);
  }

  .avatar-btn {
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
  }

  .menu {
    display: grid;
    gap: 4px;
  }

  .me {
    display: flex;
    gap: 12px;
    align-items: center;
    padding: 4px 4px 14px;
    margin-bottom: 6px;
    border-bottom: 1px solid var(--rule);
  }

  .me p {
    margin: 0;
  }

  .small {
    font-size: var(--t-sm);
  }

  .item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 12px 10px;
    border: 0;
    border-radius: var(--r-md);
    background: none;
    color: var(--ink);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .item > span:first-child {
    display: grid;
    gap: 2px;
  }

  .item:hover {
    background: var(--wash);
  }

  .label {
    font-weight: 600;
  }

  .danger .label {
    color: var(--owes, #c2410c);
  }

  .switch {
    flex: none;
    position: relative;
    width: 42px;
    height: 24px;
    border-radius: 12px;
    background: var(--rule);
    transition: background 0.15s;
  }

  .switch span {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.25);
    transition: transform 0.15s;
  }

  .switch.on {
    background: #8b5cd6;
  }

  .switch.on span {
    transform: translateX(18px);
  }
</style>
